"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";

import { requestID, baseUrl, config } from "../../../common/utils";
import { IonContent, IonPage } from "@ionic/react";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const ProjectChart: React.FC = () => {
  const [projectId, setProjectId] = useState("");
  const [chartData, setChartData] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChartData = async (id: string) => {
    try {
      setLoading(true);
      const payload = {
        requestID: requestID(),
        project_id: id,
      };
      const res = await axios.post(
        baseUrl() + "projects/getChartDataByProjectId",
        payload,
        config
      );
      console.log("Chart Data Response:", res);
      const data = res.data?.[0];
      if (data) {
        const pieFormatted = [
          {
            name: "Screened",
            value: Number.parseInt(data.total_screened || "0", 10),
          },
          {
            name: "Unscreened",
            value: Number.parseInt(data.total_unscreened || "0", 10),
          },
          {
            name: "Interviewed",
            value: Number.parseInt(data.total_interviewed || "0", 10),
          },
          {
            name: "Employed",
            value: Number.parseInt(data.total_employed || "0", 10),
          },
        ];
        setChartData(pieFormatted);
      } else {
        setChartData([]);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const payload = { requestID: requestID() };
      const res = await axios.post(
        baseUrl() + "projects/getAllProjects",
        payload,
        config
      );
      console.log(res);
      setProjectList(res.data.data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjectList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchChartData(projectId);
    }
  }, [projectId]);

  return (
    <IonPage>
      <IonContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            backgroundColor: (theme) => theme.palette.background.default, // Use MUI theme background
            p: 2,
          }}
        >
          <Card sx={{ width: "100%", maxWidth: "600px" }}>
            <CardHeader
              title={
                <Typography variant="h6" gutterBottom>
                  Project Applicant Summary (Pie Chart)
                </Typography>
              }
            />
            <CardContent>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="project-select-label">
                  Select Project
                </InputLabel>
                <Select
                  labelId="project-select-label"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value as string)}
                  label="Select Project"
                >
                  {projectList.map((project: any) => (
                    <MenuItem
                      key={project.project_id}
                      value={project.project_id}
                    >
                      {project.project_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loading && (
                <Typography
                  align="center"
                  sx={{ mt: 3, color: "text.secondary" }}
                >
                  Loading chart...
                </Typography>
              )}

              {chartData.some((item: any) => item.value > 0) && (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => {
                        const total = chartData.reduce(
                          (sum: number, entry: any) => sum + entry.value,
                          0
                        );
                        const percent = ((value / total) * 100).toFixed(1);
                        return `${name}: ${percent}%`;
                      }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}

              {!loading &&
                projectId &&
                !chartData.some((item: any) => item.value > 0) && (
                  <Typography
                    align="center"
                    sx={{ mt: 3, color: "text.secondary" }}
                  >
                    No applicant data available for this project.
                  </Typography>
                )}
            </CardContent>
          </Card>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default ProjectChart;
